import { useEffect, useMemo, useRef, useState } from "react";
import { LoadEffectData } from "./LoadEffectData.js";
import { useIsMountedRef } from "../useIsMountedRef.js";

/**
 * Helper to load data in components. Takes key that identifies
 * the request and the loading function. Triggers load function
 * only on the first render and when the key changes. The load
 * function can be inpure, unstable, and have as many side effects
 * as it wants.
 */
export function useLoadEffect<T = void>(key: string, load: () => Promise<T>): LoadEffectData<T> {
  // Breaks double load in React strict mode.
  const keyRef = useRef<string | null>(null);

  const [internalState, setInternalState] = useState<InternalState<T>>({
    state: "loading"
  });

  // Prevents state updates to unmounted components.
  const isMountedRef = useIsMountedRef();

  // Used for detecting interruptions.
  const iterationRef = useRef(0);

  const executeLoad = async () => {
    iterationRef.current++;
    // Stores the iteration number to detect interrupted
    // executions of load function.
    const currentIteration = iterationRef.current;
    try {
      setInternalState({ state: "loading" });
      const data = await load();
      if (currentIteration === iterationRef.current && isMountedRef.current) {
        // Was not interrupted.
        setInternalState({ data, state: "success" });
      }
    } catch (error) {
      if (currentIteration === iterationRef.current && isMountedRef.current) {
        // Was not interrupted.
        setInternalState({ error, state: "error" });
      }
    }
  };

  const executeLoadRef = useRef(executeLoad);
  executeLoadRef.current = executeLoad;

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      executeLoadRef.current();
    }
  }, [executeLoadRef, key]);

  return useMemo(() => ({ ...internalState, reload: executeLoadRef.current }), [internalState]);
}

/**
 * Internal state kept by the loader.
 */
type InternalState<T> = LoadingInternalState | ErrorInternalState | SuccessInternalState<T>;

type LoadingInternalState = {
  state: "loading";
};

type ErrorInternalState = {
  state: "error";
  error: unknown;
};

type SuccessInternalState<T> = {
  state: "success";
  data: T;
};
